/**
 * Admin Install Billing RPC definitions.
 *
 * Admin actions that drive the install-fee + managed-subscription
 * state machines on `agent_installations`. All endpoints require
 * admin role via `AdminMiddleware`.
 *
 * Endpoints:
 *  - `setPricing` — record fee + managed-offer pricing (status → `priced`)
 *  - `compInstall` — comp the install fee (status → `comped`)
 *  - `sendInstallInvoice` — create a Stripe invoice (status → `invoiced`)
 *  - `markInstallPaidManually` — record manual payment (status → `paid`)
 *  - `voidInstallInvoice` — void the Stripe invoice (status → `void`)
 *  - `offerManagedService` — generate Checkout URL + send the offer
 *  - `withdrawManagedOffer` — withdraw an outstanding managed offer
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";
import { InstallSummary } from "./installs.rpc.ts";

// ============================================================================
// Errors
// ============================================================================

/**
 * Returned when the install id does not resolve to a row.
 * @since 1.0.0
 */
export class InstallNotFoundError extends Schema.TaggedError<InstallNotFoundError>()(
  "InstallNotFoundError",
  {
    installId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Returned when an admin action is invoked against an install whose
 * current `installFeeStatus` does not permit the requested transition.
 *
 * @since 1.0.0
 */
export class InstallInvalidStateError extends Schema.TaggedError<InstallInvalidStateError>()(
  "InstallInvalidStateError",
  {
    installId: Schema.String,
    currentStatus: Schema.String,
    expected: Schema.Array(Schema.String),
    message: Schema.String,
  },
) {}

/**
 * Returned when a Stripe API call (invoice / checkout) fails.
 *
 * @since 1.0.0
 */
export class InstallStripeError extends Schema.TaggedError<InstallStripeError>()(
  "InstallStripeError",
  {
    installId: Schema.String,
    message: Schema.String,
  },
) {}

/**
 * Common error union for install-billing actions.
 * @since 1.0.0
 */
export const InstallBillingError = Schema.Union(
  Unauthorized,
  Forbidden,
  InstallNotFoundError,
  InstallInvalidStateError,
  InstallStripeError,
  DatabaseError,
);

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input for `setPricing`.
 * @since 1.0.0
 */
export const SetPricingInput = Schema.Struct({
  installId: Schema.String,
  installFeeAmount: Schema.Int,
  managedOfferAmount: Schema.optional(Schema.Int),
  managedOfferTerms: Schema.optional(Schema.String),
});
export type SetPricingInput = Schema.Schema.Type<typeof SetPricingInput>;

/**
 * Input for `compInstall`. `reason` is persisted as
 * `install_fee_comp_reason`.
 * @since 1.0.0
 */
export const CompInstallInput = Schema.Struct({
  installId: Schema.String,
  reason: Schema.NonEmptyTrimmedString,
});
export type CompInstallInput = Schema.Schema.Type<typeof CompInstallInput>;

/**
 * Input for actions that just need the install id.
 * @since 1.0.0
 */
export const InstallIdInput = Schema.Struct({
  installId: Schema.String,
});
export type InstallIdInput = Schema.Schema.Type<typeof InstallIdInput>;

/**
 * Input for `offerManagedService`.
 * @since 1.0.0
 */
export const OfferManagedServiceInput = Schema.Struct({
  installId: Schema.String,
  amount: Schema.Int,
  terms: Schema.NonEmptyTrimmedString,
});
export type OfferManagedServiceInput = Schema.Schema.Type<typeof OfferManagedServiceInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Install Billing RPC group.
 *
 * Tags are namespaced under `admin/installs/billing/`. All endpoints
 * require admin role via `AdminMiddleware` (the auth-aware admin
 * middleware that combines Clerk verification with role check).
 *
 * @since 1.0.0
 */
export class InstallBillingRpcs extends RpcGroup.make(
  Rpc.make("setPricing", {
    payload: SetPricingInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("compInstall", {
    payload: CompInstallInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("sendInstallInvoice", {
    payload: InstallIdInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("markInstallPaidManually", {
    payload: InstallIdInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("voidInstallInvoice", {
    payload: InstallIdInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("offerManagedService", {
    payload: OfferManagedServiceInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
  Rpc.make("withdrawManagedOffer", {
    payload: InstallIdInput,
    success: InstallSummary,
    error: InstallBillingError,
  }).middleware(AdminMiddleware),
).prefix("admin/installs/billing/") {}
